<template>
  <svg
    v-bind="$attrs"
    :aria-hidden="title ? undefined : true"
    :aria-label="title"
    class="ui-icon"
    fill="none"
    :height="size"
    viewBox="0 0 24 24"
    :width="size"
    xmlns="http://www.w3.org/2000/svg"
  >
    <title v-if="title">{{ title }}</title>
    <path
      :d="paths[name]"
      stroke="currentColor"
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-width="2"
    />
  </svg>
</template>

<script setup lang="ts">
  defineOptions({ inheritAttrs: false, name: 'UiIcon' })

  export type IconName =
    | 'AlertCircle'
    | 'AlertTriangle'
    | 'ArrowLeft'
    | 'ArrowRight'
    | 'Calendar'
    | 'Check'
    | 'CheckCircle'
    | 'ChevronDown'
    | 'ChevronLeft'
    | 'ChevronRight'
    | 'Edit05'
    | 'Eye'
    | 'FileDownload03'
    | 'HelpCircle'
    | 'HomeLine'
    | 'InfoCircle'
    | 'Mail01'
    | 'MessageChatCircle'
    | 'Minus'
    | 'Plus'
    | 'Repeat03'
    | 'Settings01'
    | 'Settings04'
    | 'Trash01'
    | 'User01'
    | 'XClose'

  withDefaults(
    defineProps<{
      name: IconName
      size?: number | string
      title?: string
    }>(),
    { size: 24 },
  )

  const paths: Record<IconName, string> = {
    AlertCircle:
      'M12 8v4m0 4h.01M22 12c0 5.5228-4.4772 10-10 10S2 17.5228 2 12 6.4772 2 12 2s10 4.4772 10 10',
    AlertTriangle:
      'M12 9v4m0 4h.01M10.615 4.891 2.39 18.616A1.6 1.6 0 0 0 3.762 21h16.476a1.6 1.6 0 0 0 1.372-2.384L13.385 4.89a1.6 1.6 0 0 0-2.77 0',
    ArrowLeft: 'M19 12H5m0 0 7 7m-7-7 7-7',
    ArrowRight: 'M5 12h14m0 0-7-7m7 7-7 7',
    Calendar:
      'M21 10H3m13-8v4M8 2v4m-.2 16h8.4c1.6802 0 2.5202 0 3.162-.327a3 3 0 0 0 1.311-1.311C21 19.7202 21 18.8802 21 17.2V8.8c0-1.6802 0-2.5202-.327-3.162a3 3 0 0 0-1.311-1.311C18.7202 4 17.8802 4 16.2 4H7.8c-1.6802 0-2.5202 0-3.162.327a3 3 0 0 0-1.311 1.311C3 6.2798 3 7.1198 3 8.8v8.4c0 1.6802 0 2.5202.327 3.162a3 3 0 0 0 1.311 1.311C5.2798 22 6.1198 22 7.8 22',
    Check: 'M20 6 9 17l-5-5',
    CheckCircle:
      'm7.5 12 3 3 6-6m5.5 3c0 5.5228-4.4772 10-10 10S2 17.5228 2 12 6.4772 2 12 2s10 4.4772 10 10',
    ChevronDown: 'm6 9 6 6 6-6',
    ChevronLeft: 'm15 18-6-6 6-6',
    ChevronRight: 'm9 18 6-6-6-6',
    Edit05:
      'M11 4H6.8c-1.6802 0-2.5202 0-3.162.327a3 3 0 0 0-1.311 1.311C2 6.2798 2 7.1198 2 8.8v8.4c0 1.6801 0 2.5202.327 3.162a3 3 0 0 0 1.311 1.311C4.2798 22 5.1198 22 6.8 22h8.4c1.6802 0 2.5202 0 3.162-.327a3 3 0 0 0 1.311-1.311C20 19.7202 20 18.8801 20 17.2V13M8 16h1.6745c.4892 0 .7338 0 .964-.0553a2 2 0 0 0 .5781-.2394c.2018-.1237.3748-.2967.7207-.6426L21.5 5.5c.8284-.8284.8284-2.1716 0-3s-2.1716-.8284-3 0l-9.5628 9.5627c-.3459.3459-.5188.5189-.6425.7207a2 2 0 0 0-.2395.5781C8 13.5917 8 13.8363 8 14.3255z',
    Eye:
      'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12m10 3a3 3 0 1 0 0-6 3 3 0 0 0 0 6',
    FileDownload03:
      'M14 2.2695v4.1306c0 .56 0 .84.109 1.054a1 1 0 0 0 .437.437C14.7599 8 15.0399 8 15.6 8h4.1305M9 15l3 3m0 0 3-3m-3 3v-6m2-10H8.8c-1.6802 0-2.5202 0-3.162.327a3 3 0 0 0-1.311 1.311C4 4.2798 4 5.1198 4 6.8v10.4c0 1.6802 0 2.5202.327 3.162a3 3 0 0 0 1.311 1.311C6.2798 22 7.1198 22 8.8 22h6.4c1.6802 0 2.5202 0 3.162-.327a3 3 0 0 0 1.311-1.311C20 19.7202 20 18.8802 20 17.2V8z',
    HelpCircle:
      'M9.09 9a2.9999 2.9999 0 0 1 5.83 1c0 2-3 3-3 3m.08 4h.01M22 12c0 5.5228-4.4772 10-10 10S2 17.5228 2 12 6.4772 2 12 2s10 4.4772 10 10',
    HomeLine:
      'M8 17h8M11.0177 2.764 4.2354 8.0391c-.4534.3527-.68.529-.8434.7498a2 2 0 0 0-.318.6502C3 9.7035 3 9.9907 3 10.565V17.8c0 1.1201 0 1.6801.218 2.108.1917.3763.4977.6823.874.874C4.5198 21 5.08 21 6.2 21h11.6c1.1201 0 1.6802 0 2.108-.218a2 2 0 0 0 .874-.874C21 19.4801 21 18.9201 21 17.8v-7.2349c0-.5744 0-.8616-.074-1.126a2 2 0 0 0-.318-.6502c-.1633-.2208-.39-.3971-.8434-.7498L12.9823 2.764c-.3513-.2732-.527-.4099-.721-.4624a1 1 0 0 0-.5226 0c-.194.0525-.3697.1891-.721.4624',
    InfoCircle:
      'M12 16v-4m0-4h.01M22 12c0 5.5228-4.4772 10-10 10S2 17.5228 2 12 6.4772 2 12 2s10 4.4772 10 10',
    Mail01:
      'm2 7 8.1649 5.7154c.6612.4629.9918.6943 1.3514.7839.3176.0792.6498.0792.9674 0 .3596-.0896.6902-.321 1.3514-.7839L22 7M6.8 20h10.4c1.6802 0 2.5202 0 3.162-.327a3 3 0 0 0 1.311-1.311C22 17.7202 22 16.8802 22 15.2V8.8c0-1.6802 0-2.5202-.327-3.162a3 3 0 0 0-1.311-1.311C19.7202 4 18.8802 4 17.2 4H6.8c-1.6802 0-2.5202 0-3.162.327a3 3 0 0 0-1.311 1.311C2 6.2798 2 7.1198 2 8.8v6.4c0 1.6802 0 2.5202.327 3.162a3 3 0 0 0 1.311 1.311C4.2798 20 5.1198 20 6.8 20',
    MessageChatCircle:
      'M6.0944 11.2288A8 8 0 0 1 6 10c0-4.4183 3.6053-8 8.0526-8s8.0526 3.5817 8.0526 8c0 .9981-.1839 1.9535-.52 2.8345-.0698.183-.1048.2745-.1206.3459a.9.9 0 0 0-.0235.1931c-.0017.0731.0082.1537.0281.3148l.4025 3.2702c.0436.354.0654.531.0065.6597a.5.5 0 0 1-.2571.2513c-.13.0559-.3065.03-.6594-.0217l-3.1852-.4669c-.1664-.0244-.2495-.0366-.3253-.0361a.9.9 0 0 0-.2001.0213c-.0741.0156-.1688.0511-.3581.122-.8833.3309-1.8406.5119-2.8404.5119a8.2 8.2 0 0 1-1.2299-.0927M7.6316 22C10.5965 22 13 19.5376 13 16.5S10.5965 11 7.6316 11s-5.3684 2.4624-5.3684 5.5c0 .6106.097 1.1979.2764 1.7467.0757.232.1136.348.126.4272a.85.85 0 0 1 .0105.2128c-.0046.0801-.0247.1706-.0648.3516L2 22l2.9948-.409c.1635-.0223.2452-.0335.3166-.033a.84.84 0 0 1 .1887.0193c.07.0139.174.0506.3822.1241A5.25 5.25 0 0 0 7.6316 22',
    Minus: 'M5 12h14',
    Plus: 'M12 5v14m-7-7h14',
    Repeat03:
      'M13 22 10 19m0 0 3-3m-3 3h5c3.866 0 7-3.134 7-7 0-2.7924-1.6351-5.2029-4-6.3264M6 18.3264C3.6351 17.2029 2 14.7924 2 12c0-3.866 3.134-7 7-7h5m0 0-3-3m3 3-3 3',
    Settings01:
      'M12 15c1.6569 0 3-1.3431 3-3s-1.3431-3-3-3-3 1.3431-3 3 1.3431 3 3 3M18.7273 14.7273a1.5 1.5 0 0 0 .3 1.6545l.0545.0546a1.818 1.818 0 0 1 0 2.5727 1.8184 1.8184 0 0 1-2.5727 0l-.0546-.0546a1.5 1.5 0 0 0-1.6545-.3 1.5 1.5 0 0 0-.9091 1.3728v.1545a1.8182 1.8182 0 1 1-3.6364 0V20.1a1.5 1.5 0 0 0-.9818-1.3727 1.5 1.5 0 0 0-1.6545.3l-.0546.0545a1.818 1.818 0 0 1-1.9826.3947 1.82 1.82 0 0 1-.59-.3947 1.818 1.818 0 0 1-.5333-1.2863 1.818 1.818 0 0 1 .5332-1.2864l.0545-.0546a1.5 1.5 0 0 0 .3-1.6545 1.5 1.5 0 0 0-1.3727-.9091h-.1545a1.818 1.818 0 0 1-1.2857-3.1038 1.818 1.818 0 0 1 1.2857-.5326H3.9a1.5 1.5 0 0 0 1.3727-.9818 1.5 1.5 0 0 0-.3-1.6545l-.0545-.0546a1.818 1.818 0 0 1 0-2.5727 1.818 1.818 0 0 1 2.5727 0l.0545.0545a1.5 1.5 0 0 0 1.6546.3h.0727a1.5 1.5 0 0 0 .9091-1.3727v-.1545a1.8182 1.8182 0 0 1 3.6364 0V3.9a1.5 1.5 0 0 0 .9091 1.3727 1.5 1.5 0 0 0 1.6545-.3l.0546-.0545a1.817 1.817 0 0 1 1.2863-.5333 1.818 1.818 0 0 1 1.2864.5333 1.819 1.819 0 0 1 .3946 1.9826 1.82 1.82 0 0 1-.3946.5901l-.0546.0545a1.501 1.501 0 0 0-.3 1.6546v.0727a1.5 1.5 0 0 0 1.3728.9091h.1545a1.8183 1.8183 0 0 1 0 3.6364H20.1a1.5 1.5 0 0 0-1.3727.9091',
    Settings04:
      'M3 8h12m0 0c0 1.6569 1.3431 3 3 3s3-1.3431 3-3-1.3431-3-3-3-3 1.3431-3 3m-6 8h12M9 16c0 1.6569-1.3431 3-3 3s-3-1.3431-3-3 1.3431-3 3-3 3 1.3431 3 3',
    Trash01:
      'M16 6v-.8c0-1.1201 0-1.6802-.218-2.108a2 2 0 0 0-.874-.874C14.4802 2 13.9201 2 12.8 2h-1.6c-1.1201 0-1.6802 0-2.108.218a2 2 0 0 0-.874.874C8 3.5198 8 4.08 8 5.2V6m2 5.5v5m4-5v5M3 6h18m-2 0v11.2c0 1.6802 0 2.5202-.327 3.162a3 3 0 0 1-1.311 1.311C16.7202 22 15.8802 22 14.2 22H9.8c-1.6802 0-2.5202 0-3.162-.327a3 3 0 0 1-1.311-1.311C5 19.7202 5 18.8802 5 17.2V6',
    User01:
      'M20 21c0-1.3956 0-2.0933-.1722-2.6611a4 4 0 0 0-2.6667-2.6667C16.5933 15.5 15.8956 15.5 14.5 15.5h-5c-1.3956 0-2.0933 0-2.6611.1722a4 4 0 0 0-2.6667 2.6667C4 18.9067 4 19.6044 4 21M16.5 7.5c0 2.4853-2.0147 4.5-4.5 4.5S7.5 9.9853 7.5 7.5 9.5147 3 12 3s4.5 2.0147 4.5 4.5',
    XClose: 'M18 6 6 18M6 6l12 12',
  }
</script>

<style scoped>
  .ui-icon {
    display: inline-block;
    flex: none;
    vertical-align: middle;
    color: inherit;
  }
</style>
